import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clapperboard, Clock, Archive, BarChart3, ArrowRight, Loader2 } from "lucide-react";

function CustomGoogleButton({ clientId, onError }) {
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <GoogleCodeButton onError={onError} />
    </GoogleOAuthProvider>
  );
}

function GoogleCodeButton({ onError }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (resp) => {
      setBusy(true);
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const { data } = await api.post("/auth/google", { code: resp.code, redirect_uri: "postmessage" });
        setUser(data);
        navigate("/dashboard", { replace: true, state: { user: data } });
      } catch (e) {
        onError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    onError: () => onError("Google sign-in was cancelled or failed. Please try again."),
  });
  return (
    <Button
      data-testid="sign-in-button"
      onClick={() => login()}
      disabled={busy}
      size="lg"
      className="mt-8 w-full h-12 text-base gap-2 group"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      Continue with Google
      {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
    </Button>
  );
}

export default function Login() {
  const { user, loading } = useAuth();
  const [authCfg, setAuthCfg] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    api.get("/auth/config").then((r) => setAuthCfg(r.data)).catch(() => setAuthCfg({ custom_google: false }));
  }, []);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Clapperboard, title: "Creative Workflow", desc: "Plan, create, review, approve, and complete creative work." },
    { icon: Clock, title: "Daily Tracker", desc: "Track daily activities, progress, and outcomes." },
    { icon: Archive, title: "Creative Archive", desc: "Keep a permanent record of completed creative work." },
    { icon: BarChart3, title: "Dashboard", desc: "See workload, progress, deadlines, and what needs attention." },
  ];

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col lg:flex-row">
      {/* Left brand / hero */}
      <div className="relative lg:w-1/2 bg-[#0B0F17] text-white flex flex-col justify-between p-8 sm:p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{ background: "radial-gradient(600px circle at 20% 20%, rgba(232,81,34,0.35), transparent 60%), radial-gradient(500px circle at 80% 80%, rgba(232,81,34,0.18), transparent 60%)" }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white p-2 shadow-sm">
              <img src="/livelife-logo.webp" alt="LiveLife Education" className="h-8 w-auto" data-testid="login-logo" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">LiveLife Education Intranet</span>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-xs font-medium uppercase tracking-widest text-[#e85122] mb-4">LiveLife Education Intranet</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            One place to manage creative work.
          </h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">
            Plan, create, review, approve, and track your work in one system.
          </p>
        </div>
        <div className="relative z-10 text-white/40 text-xs">© 2026 LiveLife Education</div>
      </div>

      {/* Right sign-in */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <img src="/livelife-logo.webp" alt="LiveLife Education" className="h-10 w-auto mb-6 lg:hidden" />
          <h2 className="font-display text-3xl font-bold tracking-tight">Sign in to continue</h2>
          <p className="mt-2 text-muted-foreground">Use your Google account to access your workspace.</p>

          {authCfg?.custom_google ? (
            <CustomGoogleButton clientId={authCfg.client_id} onError={setAuthError} />
          ) : (
            <Button
              data-testid="sign-in-button"
              onClick={handleSignIn}
              size="lg"
              className="mt-8 w-full h-12 text-base gap-2 group"
            >
              Continue with Google
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
          {authError && <p data-testid="sign-in-error" className="mt-3 text-sm text-rose-600">{authError}</p>}

          <div className="mt-10 grid grid-cols-2 gap-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-4">
                <f.icon className="h-5 w-5 text-primary" />
                <p className="mt-2 font-semibold text-sm">{f.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
